import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  programa: any;
}

const ModalAsignaciones: React.FC<Props> = ({ isOpen, onClose, programa }) => {
  const [beneficiarioId, setBeneficiarioId] = useState('');
  const [busquedaBeneficiario, setBusquedaBeneficiario] = useState('');
  const [beneficiariosCatalogo, setBeneficiariosCatalogo] = useState<any[]>([]);
  const [motivoBaja, setMotivoBaja] = useState('');
  const [data, setData] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>(null);

  const fetchData = async () => {
    if (!programa?.id) return;
    try {
      const res = await axiosInstance.get(`/desarrollo-social/programas/${programa.id}`);
      setData(res.data?.asignaciones || []);
      setMeta(res.data?.meta || null);
    } catch {
      setData([]);
      setMeta(null);
    }
  };

  const fetchBeneficiarios = async () => {
    try {
      const res = await axiosInstance.get('/desarrollo-social/beneficiarios');
      setBeneficiariosCatalogo(res.data || []);
    } catch {
      setBeneficiariosCatalogo([]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
      fetchBeneficiarios();
    }
  }, [isOpen, programa?.id]);

  const textoBusqueda = busquedaBeneficiario.trim().toLowerCase();
  const idsAsignadosActivos = new Set(
    data.filter((item) => item.estatus === 'asignado').map((item) => Number(item.beneficiarioId))
  );

  const beneficiariosDisponibles = beneficiariosCatalogo.filter((b) => {
    const matchBusqueda = !textoBusqueda
      || String(b.nombreCompleto || '').toLowerCase().includes(textoBusqueda)
      || String(b.curp || '').toLowerCase().includes(textoBusqueda)
      || String(b.folio || '').toLowerCase().includes(textoBusqueda)
      || String(b.id).includes(textoBusqueda);
    return matchBusqueda && !idsAsignadosActivos.has(Number(b.id));
  });

  const handleAsignar = async () => {
    if (!beneficiarioId) return;
    try {
      await axiosInstance.post(`/desarrollo-social/programas/${programa.id}/asignaciones`, {
        beneficiarioId: Number(beneficiarioId),
      });
      setBeneficiarioId('');
      fetchData();
    } catch {
      // noop
    }
  };

  const handleBaja = async (id: number) => {
    if (!motivoBaja) return;
    try {
      await axiosInstance.put(`/desarrollo-social/programas/asignaciones/${id}/baja`, {
        motivoBaja,
      });
      setMotivoBaja('');
      fetchData();
    } catch {
      // noop
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="xl" overflow="hidden">
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" bg="gray.50">
          Asignaciones - {programa?.nombre}
        </ModalHeader>
        <ModalBody py={5} bg="white">
          <HStack justify="space-between" mb={4} flexWrap="wrap">
            <Text>Meta: {meta?.metaProgramada || 0} | Asignados: {meta?.asignadosActivos || 0} | Disponibles: {meta?.disponibles || 0}</Text>
          </HStack>

          <VStack align="stretch" spacing={4} mb={4}>
            <HStack spacing={4} align="flex-end" flexWrap="wrap">
              <FormControl minW={{ base: '100%', md: '260px' }}>
                <FormLabel>Buscar beneficiario</FormLabel>
                <Input
                  placeholder="Nombre, CURP, folio o ID"
                  value={busquedaBeneficiario}
                  onChange={(e) => setBusquedaBeneficiario(e.target.value)}
                />
                <FormHelperText>Se listan beneficiarios ya cargados en el sistema.</FormHelperText>
              </FormControl>
              <FormControl minW={{ base: '100%', md: '420px' }}>
                <FormLabel>Beneficiario</FormLabel>
                <Select
                  placeholder={beneficiariosDisponibles.length ? 'Selecciona beneficiario' : 'Sin resultados'}
                  value={beneficiarioId}
                  onChange={(e) => setBeneficiarioId(e.target.value)}
                >
                  {beneficiariosDisponibles.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.folio || `ID ${b.id}`} - {b.nombreCompleto} {b.curp ? `(${b.curp})` : ''}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleAsignar} isDisabled={!beneficiarioId}>
                Asignar
              </Button>
            </HStack>
          </VStack>

          <Table size="sm">
            <Thead bg="#5b182b">
              <Tr>
                <Th color="white">Beneficiario</Th>
                <Th color="white">Estatus</Th>
                <Th color="white">Fecha</Th>
                <Th color="white">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.beneficiario?.nombreCompleto || `ID ${item.beneficiarioId}`}</Td>
                  <Td>{item.estatus}</Td>
                  <Td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString('es-MX') : '-'}</Td>
                  <Td>
                    {item.estatus === 'asignado' && (
                      <HStack>
                        <Input placeholder="Motivo baja" value={motivoBaja} onChange={(e) => setMotivoBaja(e.target.value)} />
                        <Button size="xs" colorScheme="red" onClick={() => handleBaja(item.id)}>Baja</Button>
                      </HStack>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </ModalBody>
        <ModalFooter borderTop="1px solid" borderColor="gray.100" bg="gray.50">
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalAsignaciones;


